import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from 'src/common/utils/api';
import { ProposalDiffLine, ProposedActionStatus, SkillProposedAction } from '../chat/types';

type SkillProposalResponse = {
  id: string;
  summary: string;
  status: ProposedActionStatus;
  failureReason: string | null;
  operation: 'CREATE' | 'UPDATE' | 'DELETE' | 'SET_ENABLED';
  skillName: string;
  newName: string | null;
  addedCount: number;
  removedCount: number;
  diffLines: ProposalDiffLine[];
  diffTruncated: boolean;
};

export function toSkillProposedAction(proposal: SkillProposalResponse): SkillProposedAction {
  return {
    kind: 'SKILL',
    proposalId: proposal.id,
    operation: proposal.operation,
    skillName: proposal.skillName,
    newName: proposal.newName,
    summary: proposal.summary,
    addedCount: proposal.addedCount,
    removedCount: proposal.removedCount,
    diffLines: proposal.diffLines,
    diffTruncated: proposal.diffTruncated,
    status: proposal.status,
    failureReason: proposal.failureReason,
  };
}

export function usePendingSkillProposals(conversationId: string | undefined, enabled: boolean) {
  const { data, isError } = useQuery<SkillProposalResponse[]>({
    queryKey: ['conversations', conversationId, 'skill-proposals', { status: 'PENDING' }],
    enabled: enabled && !!conversationId,
    // Flag/role gating means a 404/403 during rollout; never retry or surface it as an error.
    retry: false,
  });
  return { pendingProposals: isError ? [] : (data ?? []) };
}

export function useSkillProposalDecision(conversationId: string | undefined) {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending } = useMutation<
    { data: SkillProposalResponse },
    Error,
    { proposalId: string; decision: 'APPROVE' | 'DENY' }
  >({
    mutationFn: ({ proposalId, decision }) =>
      api.post<SkillProposalResponse>(
        `conversations/${conversationId}/skill-proposals/${proposalId}/decision`,
        { decision },
      ),
    onSuccess: response => {
      if (response.data.status === 'APPLIED') {
        // The approved change altered the tenant's custom skills; the admin pages key their
        // queries under ['tenant', 'custom-skills'].
        queryClient.invalidateQueries({ queryKey: ['tenant', 'custom-skills'] });
      }
    },
    // Invalidate on failure too: a 409/404 means the proposal was decided elsewhere, and the
    // pending list must refresh so an orphan-recovered widget disappears rather than staying
    // clickable.
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ['conversations', conversationId, 'skill-proposals'],
      });
    },
  });
  return { decideSkillProposal: mutateAsync, isDecidingSkill: isPending };
}
